// Pure mapper: authoritative BackendState (snake_case JSON) → an ExploreScene-friendly view
// (camelCase + Set/Map lookups). Wired in via JKGame_MatchController.cacheBackendView when the
// backend flag is on; getPlayerView/getOccupantsAt read from the mapped view. Framework-free.

import type { BackendOccupant, BackendPlayer, BackendState } from './JKGame_api'

export interface BackendView {
  status: 'running' | 'over'
  winReason: 'survivor' | 'escape' | 'dead' | null
  step: number
  weather: number
  stepsToShrink: number
  rank: number
  alive: number
  total: number
  player: BackendPlayer
  forbidden: Set<number>
  nextForbidden: Set<number> // zones forbidden at the next shrink (danger preview)
  injurySet: Set<string> // player injuries as a Set, mirroring local Entity.injuries
  occupantsByZone: Map<number, BackendOccupant[]>
  entitiesById: Map<string, BackendOccupant> // flat lookup (combat target etc.)
  pendingTargetId: string | null
  pendingBoon: string[] | null // three-choice run-buff offer (after a kill), or null
  boons: string[] // run buffs taken this match
}

export function toExploreView(s: BackendState): BackendView {
  const occupantsByZone = new Map<number, BackendOccupant[]>()
  const entitiesById = new Map<string, BackendOccupant>()
  for (const e of s.entities) {
    entitiesById.set(e.id, e)
    if (!e.alive || e.id === 'P') continue
    const list = occupantsByZone.get(e.zone)
    if (list) list.push(e)
    else occupantsByZone.set(e.zone, [e])
  }
  return {
    status: s.status,
    winReason: s.win_reason,
    step: s.step,
    weather: s.weather,
    stepsToShrink: s.steps_to_shrink,
    rank: s.rank,
    alive: s.alive,
    total: s.total,
    player: s.player,
    forbidden: new Set(s.forbidden),
    nextForbidden: new Set(s.next_forbidden),
    injurySet: new Set(s.player.injuries),
    occupantsByZone,
    entitiesById,
    pendingTargetId: s.pending_target_id,
    pendingBoon: s.pending_boon ?? null,
    boons: s.boons ?? [],
  }
}

